import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Plus, Sparkles, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const jhdr = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };
const ADD = "__vidura_add__";
const SKIP_RE = /gstin|\bpan\b|ifsc|otp|passcode|password|e-?mail|phone|mobile|whatsapp|\burl\b|website|link|search|amount|qty|quantity|\bdate\b|\brate\b|price|number|code|token|slug|₹|\$|%/i;
const MODES = [["create", "Create"], ["improve", "Improve"], ["shorten", "Shorten"], ["expand", "Expand"], ["formal", "Formal"], ["simplify", "Simplify"], ["hinglish", "Hinglish"]];

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
const txt = (el) => (el?.textContent || "").replace(/\s+/g, " ").trim();
const labelOf = (el) => {
  const own = el.labels?.[0] ? txt(el.labels[0]) : "";
  if (own) return own.replace(/[*:]+$/, "").trim();
  const aria = el.getAttribute("aria-label") || el.getAttribute("title");
  if (aria) return aria;
  let n = el.previousElementSibling;
  for (let i = 0; i < 2 && n; i++, n = n.previousElementSibling) if (/^(LABEL|P|SPAN|H[1-6])$/.test(n.tagName) && txt(n).length < 80 && txt(n)) return txt(n);
  const wrap = el.closest("label");
  if (wrap) { const t = txt(wrap).replace(txt(el), "").trim(); if (t) return t.slice(0, 80); }
  const p = el.parentElement, pp = p?.parentElement;
  const cand = [...(p?.children || []), ...(pp?.children || [])].find((c) => c !== el && c !== p && /^(LABEL|P|SPAN|H[1-6])$/.test(c.tagName) && txt(c) && txt(c).length < 80);
  if (cand) return txt(cand);
  return el.placeholder || el.name || (el.dataset.testid || "").replace(/[-_]+/g, " ") || (el.tagName === "SELECT" ? "option" : "text");
};
const fieldKey = (el, path) => slug(el.dataset.testid ? `t-${el.dataset.testid}` : `${path.split("/").slice(1, 3).join("-")}-${el.name || labelOf(el)}`);

const eligibleText = (el) => {
  if (!el || el.closest("[data-vidura-ui],[data-no-vidura]") || el.readOnly || el.disabled) return false;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT" || el.type !== "text" || el.list) return false;
  if ((el.maxLength > 0 && el.maxLength <= 24) || el.pattern || /numeric|decimal|tel|email|url/.test(el.inputMode || "")) return false;
  if (SKIP_RE.test(`${el.name} ${el.placeholder} ${el.dataset.testid || ""} ${labelOf(el)}`)) return false;
  return el.getBoundingClientRect().width >= 140;
};
const eligibleSelect = (el) => el?.tagName === "SELECT" && !el.multiple && !el.disabled && !el.closest("[data-vidura-ui],[data-no-vidura]")
  && ![...el.options].some((o) => /^__(add|other)/.test(o.value)) && el.getBoundingClientRect().width >= 60;

const setNative = (el, v) => {
  const proto = el.tagName === "TEXTAREA" ? HTMLTextAreaElement.prototype : el.tagName === "SELECT" ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value").set.call(el, v);
  if (el.tagName !== "SELECT") el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
};
const collectContext = (el, path) => {
  const box = el.closest("form,[role=dialog],section,article") || el.parentElement?.parentElement?.parentElement || document.body;
  const heading = txt(box.querySelector("h1,h2,h3,h4")) || txt(document.querySelector("main h1, h1"));
  const siblings = [...box.querySelectorAll("input,textarea,select")].filter((f) => f !== el && !/password|hidden|file/.test(f.type) && f.value && !f.closest("[data-vidura-ui]"))
    .slice(0, 20).map((f) => ({ label: labelOf(f), value: f.tagName === "SELECT" ? (f.selectedOptions[0]?.text || f.value) : f.value.slice(0, 200) }));
  return { field: labelOf(el), multiline: el.tagName === "TEXTAREA", page: { title: document.title, path, heading: heading.slice(0, 120) }, siblings };
};

const TextPanel = ({ el, path, side, onClose }) => {
  const [mode, setMode] = useState(el.value.trim() ? "improve" : "create");
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [aid, setAid] = useState(null);
  const [rated, setRated] = useState(false);
  const label = labelOf(el);
  const run = async () => {
    setBusy(true); setRated(false);
    const body = JSON.stringify({ field_key: "vidura_field", context: { ...collectContext(el, path), mode, instruction: instr }, current_text: el.value });
    try {
      let r = await fetch(`${API}/${side}/ai/assist`, { method: "POST", headers: jhdr, credentials: "include", body });
      if ((r.status === 401 || r.status === 403) && side === "team") r = await fetch(`${API}/client/ai/assist`, { method: "POST", headers: jhdr, credentials: "include", body });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Vidura could not write this right now");
      setOut(d.text); setAid(d.assist_id);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const use = () => { setNative(el, out); el.focus(); toast.success("Vidura filled it in — edit freely"); onClose(); };
  const rate = async (rating) => {
    setRated(true);
    fetch(`${API}/${side}/ai/feedback`, { method: "POST", headers: jhdr, credentials: "include", body: JSON.stringify({ assist_id: aid, rating, final_text: rating === "up" ? out : null }) }).catch(() => {});
    if (rating === "down") run();
  };
  return (
    <div className="w-[340px] bg-gf-surface border border-gf-primary/40 shadow-2xl p-3 font-body" data-testid="vidura-field-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-wider text-gf-primary inline-flex items-center gap-1.5 truncate"><Sparkles className="w-3 h-3" /> Vidura · {label}</p>
        <button type="button" onClick={onClose} className="text-gf-muted hover:text-gf-text" data-testid="vidura-field-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex flex-wrap gap-1 mt-2.5">
        {MODES.filter(([m]) => m === "create" || el.value.trim()).map(([m, l]) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`vidura-mode-${m}`}
            className={`font-mono text-[9px] uppercase tracking-wider border px-2 py-1 ${mode === m ? "border-gf-primary text-gf-primary bg-gf-primary/10" : "border-white/15 text-gf-muted hover:text-gf-text"}`}>{l}</button>
        ))}
      </div>
      <input value={instr} onChange={(e) => setInstr(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") run(); }} placeholder="Tell Vidura what you need (optional)"
        className="mt-2.5 w-full bg-gf-bg border border-white/15 px-2.5 py-1.5 text-xs text-gf-text focus:outline-none focus:border-gf-primary" data-testid="vidura-field-instruction" />
      {out !== null && (
        <div className="mt-2.5 border border-white/10 bg-gf-bg/60 p-2.5 max-h-44 overflow-y-auto" data-testid="vidura-field-output">
          <p className="text-xs text-gf-text whitespace-pre-wrap leading-relaxed">{out}</p>
        </div>
      )}
      <div className="flex items-center gap-2 mt-2.5">
        <button type="button" onClick={run} disabled={busy} data-testid="vidura-field-generate"
          className="inline-flex items-center gap-1.5 bg-gf-primary text-gf-bg font-bold text-xs px-3 py-1.5 hover:bg-gf-primary-hover disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {out === null ? `Vidura, ${mode}` : "Try again"}
        </button>
        {out !== null && !busy && (
          <>
            <button type="button" onClick={use} className="border border-gf-primary/50 text-gf-primary font-bold text-xs px-3 py-1.5 hover:bg-gf-primary/10" data-testid="vidura-field-use">Use this</button>
            {aid && !rated && (
              <span className="ml-auto inline-flex gap-1.5">
                <button type="button" onClick={() => rate("up")} className="text-gf-muted hover:text-gf-primary" title="Good" data-testid="vidura-field-up"><ThumbsUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => rate("down")} className="text-gf-muted hover:text-red-400" title="Not good — try again" data-testid="vidura-field-down"><ThumbsDown className="w-3.5 h-3.5" /></button>
              </span>
            )}
          </>
        )}
      </div>
      <p className="font-mono text-[9px] text-gf-muted mt-2">Uses this page and the other filled fields as context. Review before saving.</p>
    </div>
  );
};

const AddOptionPanel = ({ el, path, side, onClose }) => {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const val = v.trim();
    if (!val) return;
    setBusy(true);
    const key = fieldKey(el, path);
    try {
      let r = await fetch(`${API}/${side}/field-options/${key}`, { method: "POST", headers: jhdr, credentials: "include", body: JSON.stringify({ value: val, label: labelOf(el), path }) });
      if ((r.status === 401 || r.status === 403) && side === "team") r = await fetch(`${API}/client/field-options/${key}`, { method: "POST", headers: jhdr, credentials: "include", body: JSON.stringify({ value: val, label: labelOf(el), path }) });
      if (!r.ok) throw new Error("Saved for this session only");
      window.__viduraOpts?.set(key, [...(window.__viduraOpts.get(key) || []), val]);
    } catch (e) { toast.message(e.message); }
    if (![...el.options].some((o) => o.value === val)) {
      const o = document.createElement("option"); o.value = val; o.textContent = val; o.dataset.viduraOpt = "1";
      const add = [...el.options].find((x) => x.value === ADD);
      add ? el.insertBefore(o, add) : el.appendChild(o);
    }
    setNative(el, val);
    toast.success(`"${val}" added to ${labelOf(el)}`);
    setBusy(false); onClose();
  };
  return (
    <div className="w-[300px] bg-gf-surface border border-gf-primary/40 shadow-2xl p-3 font-body" data-testid="vidura-add-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-wider text-gf-primary inline-flex items-center gap-1.5 truncate"><Plus className="w-3 h-3" /> Add option · {labelOf(el)}</p>
        <button type="button" onClick={onClose} className="text-gf-muted hover:text-gf-text" data-testid="vidura-add-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-1.5 mt-2.5">
        <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }} placeholder="New option…"
          className="flex-1 bg-gf-bg border border-white/15 px-2.5 py-1.5 text-xs text-gf-text focus:outline-none focus:border-gf-primary" data-testid="vidura-add-input" />
        <button type="button" onClick={save} disabled={busy || !v.trim()} className="bg-gf-primary text-gf-bg font-bold text-xs px-3 disabled:opacity-50" data-testid="vidura-add-save">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}</button>
      </div>
      <p className="font-mono text-[9px] text-gf-muted mt-2">Saved to this dropdown for everyone on the team.</p>
    </div>
  );
};

export const ViduraFieldAssist = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const side = pathname.startsWith("/clientportal") ? "client" : "team";
  const [target, setTarget] = useState(null);
  const [panel, setPanel] = useState(null);
  const [rect, setRect] = useState(null);
  const panelRef = useRef(null);
  const enabled = Boolean(user);

  const place = useCallback(() => { if (target?.isConnected) setRect(target.getBoundingClientRect()); else { setTarget(null); setPanel(null); } }, [target]);
  useEffect(() => { place(); }, [target, place]);
  useEffect(() => {
    if (!target) return;
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [target, place]);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = (e) => {
      const el = e.target;
      if (eligibleText(el) || eligibleSelect(el)) { if (el.tagName === "SELECT") el.__viduraPrev = el.value; setTarget(el); setPanel(null); }
      else if (!el.closest?.("[data-vidura-ui]")) { setTarget((t) => (panelRef.current ? t : null)); }
    };
    const onChangeCapture = (e) => {
      const el = e.target;
      if (el.tagName !== "SELECT" || el.value !== ADD) { if (el.tagName === "SELECT") el.__viduraPrev = el.value; return; }
      e.stopImmediatePropagation(); e.preventDefault();
      el.value = el.__viduraPrev ?? "";
      setTarget(el); setPanel("add");
    };
    const onDown = (e) => { if (!e.target.closest("[data-vidura-ui]") && e.target !== target && !eligibleText(e.target) && !eligibleSelect(e.target)) { setPanel(null); if (document.activeElement !== target) setTarget(null); } };
    const onKey = (e) => { if (e.key === "Escape") setPanel(null); };
    document.addEventListener("focusin", onFocus);
    document.addEventListener("change", onChangeCapture, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("focusin", onFocus); document.removeEventListener("change", onChangeCapture, true); document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [enabled, target]);

  // Inject saved options + "＋ Add option…" into every eligible <select>
  useEffect(() => {
    if (!enabled) return;
    window.__viduraOpts = window.__viduraOpts || new Map();
    const cache = window.__viduraOpts;
    let timer = null;
    const pending = new Set();
    const decorate = (sel) => {
      if (!eligibleSelect(sel) && ![...sel.options].some((o) => o.value === ADD)) return;
      const key = fieldKey(sel, pathname);
      sel.dataset.viduraKey = key;
      const saved = cache.get(key);
      if (saved === undefined) { pending.add(key); }
      else saved.forEach((v) => { if (![...sel.options].some((o) => o.value === v)) { const o = document.createElement("option"); o.value = v; o.textContent = v; o.dataset.viduraOpt = "1"; sel.appendChild(o); } });
      let add = [...sel.options].find((o) => o.value === ADD);
      if (!add) { add = document.createElement("option"); add.value = ADD; add.textContent = "＋ Add option…"; add.dataset.viduraAdd = "1"; }
      if (sel.lastElementChild !== add) sel.appendChild(add);
    };
    const flush = async () => {
      if (!pending.size) return;
      const keys = [...pending]; pending.clear();
      keys.forEach((k) => cache.set(k, []));
      try {
        let r = await fetch(`${API}/${side}/field-options?keys=${encodeURIComponent(keys.join(","))}`, { credentials: "include" });
        if ((r.status === 401 || r.status === 403) && side === "team") r = await fetch(`${API}/client/field-options?keys=${encodeURIComponent(keys.join(","))}`, { credentials: "include" });
        const d = await r.json();
        Object.entries(d.options || {}).forEach(([k, v]) => cache.set(k, v));
        document.querySelectorAll("select").forEach(decorate);
      } catch { /* offline: sentinel only */ }
    };
    const scan = () => { document.querySelectorAll("select").forEach(decorate); flush(); };
    const mo = new MutationObserver((muts) => {
      if (muts.every((m) => [...m.addedNodes, ...m.removedNodes].every((n) => n.dataset?.viduraAdd || n.dataset?.viduraOpt))) return;
      clearTimeout(timer); timer = setTimeout(scan, 120);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    scan();
    return () => { mo.disconnect(); clearTimeout(timer); };
  }, [enabled, pathname, side]);

  if (!enabled || !target || !rect) return null;
  const isSelect = target.tagName === "SELECT";
  const top = Math.max(8, rect.top - 11);
  const left = Math.min(window.innerWidth - 120, rect.right - (isSelect ? 104 : 78));
  const panelTop = rect.bottom + 6 + 360 > window.innerHeight ? Math.max(8, rect.top - 6 - (panel === "add" ? 110 : 300)) : rect.bottom + 6;
  const panelLeft = Math.max(8, Math.min(window.innerWidth - 350, rect.right - 340));
  return (
    <div data-vidura-ui="1">
      {!panel && (
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setPanel(isSelect ? "add" : "text")}
          style={{ position: "fixed", top, left, zIndex: 9990 }} data-testid={isSelect ? "vidura-add-pill" : "vidura-pill"}
          className="inline-flex items-center gap-1 bg-gf-surface border border-gf-primary/50 text-gf-primary font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 shadow-lg hover:bg-gf-primary hover:text-gf-bg transition-colors">
          {isSelect ? <><Plus className="w-3 h-3" /> Add option</> : <><Sparkles className="w-3 h-3" /> Vidura</>}
        </button>
      )}
      {panel && (
        <div ref={panelRef} style={{ position: "fixed", top: panelTop, left: panelLeft, zIndex: 9991 }}>
          {panel === "text" ? <TextPanel el={target} path={pathname} side={side} onClose={() => setPanel(null)} /> : <AddOptionPanel el={target} path={pathname} side={side} onClose={() => setPanel(null)} />}
        </div>
      )}
    </div>
  );
};
